type LogLevel = 'info' | 'warn' | 'error' | 'debug';

interface LogEntry {
  level: LogLevel;
  message: string;
  data?: any;
  timestamp: string;
  component?: string;
}

interface LogContext {
  [key: string]: any;
}

class Logger {
  private isDevelopment = import.meta.env.DEV;
  private logs: LogEntry[] = [];
  private maxLogs = 1000;

  private sanitizeContext(context: LogContext): LogContext {
    const sensitive = ['password', 'token', 'secret', 'key', 'credential', 'auth', 'session'];
    const sanitized: LogContext = {};
    
    for (const [key, value] of Object.entries(context)) {
      const keyLower = key.toLowerCase();
      const isSensitive = sensitive.some(term => keyLower.includes(term));
      
      if (isSensitive) {
        sanitized[key] = '[REDACTED]';
      } else if (typeof value === 'object' && value !== null) {
        sanitized[key] = this.sanitizeContext(value);
      } else {
        sanitized[key] = value;
      }
    }
    
    return sanitized;
  }

  private formatMessage(level: LogLevel, message: string, data?: any, component?: string) {
    const timestamp = new Date().toISOString();
    const sanitizedData = data && typeof data === 'object' ? this.sanitizeContext(data) : data;
    
    const entry: LogEntry = {
      level,
      message,
      data: sanitizedData,
      timestamp,
      component
    };
    
    // Store in memory (limited)
    this.logs.push(entry);
    if (this.logs.length > this.maxLogs) {
      this.logs.shift();
    }
    
    return entry;
  }

  info(message: string, data?: any, component?: string) {
    const entry = this.formatMessage('info', message, data, component);
    if (this.isDevelopment) {
      console.log(`ℹ️ [${component || 'APP'}] ${message}`, entry.data || '');
    }
    return entry;
  }

  warn(message: string, data?: any, component?: string) {
    const entry = this.formatMessage('warn', message, data, component);
    if (this.isDevelopment) {
      console.warn(`⚠️ [${component || 'APP'}] ${message}`, entry.data || '');
    }
    return entry;
  }

  error(message: string, error?: any, component?: string) {
    const entry = this.formatMessage('error', message, error, component);
    
    // Always log errors but sanitize in production
    if (this.isDevelopment) {
      console.error(`❌ [${component || 'APP'}] ${message}`, entry.data || '');
    } else {
      console.error(`❌ [${component || 'APP'}] ${message}`, '[Error details hidden in production]');
    }
    
    // In production, send to error tracking service
    if (!this.isDevelopment) {
      this.sendToErrorService(entry);
    }
    
    return entry;
  }

  debug(message: string, data?: any, component?: string) {
    const entry = this.formatMessage('debug', message, data, component);
    if (this.isDevelopment) {
      console.debug(`🐛 [${component || 'APP'}] ${message}`, entry.data || '');
    }
    return entry;
  }

  private sendToErrorService(entry: LogEntry) {
    try {
      if (typeof window !== 'undefined' && window.navigator.sendBeacon) {
        window.navigator.sendBeacon('/api/logs', JSON.stringify(entry));
      }
    } catch (err) {
      // Fail silently to prevent error loops
    }
  }

  getLogs() {
    return this.logs;
  }

  clearLogs() {
    this.logs = [];
  }
}

export const logger = new Logger();
export default logger;
